import { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';

import { Dialog, Tooltip } from 'components';
import { AttachmentImage } from 'pages/Dashboard/AttachmentLoading';

function ZoomableImage({ src, name }: { src: string; name: string }) {
	const [zoom, setZoom] = useState(1);
	const viewport = useRef<HTMLDivElement>(null);
	const drag = useRef<{ pointerId: number; x: number; y: number; left: number; top: number } | null>(null);
	const [dragging, setDragging] = useState(false);
	const changeZoom = (factor: number) => setZoom((value) => Math.min(5, Math.max(1, value * factor)));

	useEffect(() => {
		const element = viewport.current;
		if (!element) return;
		const onWheel = (event: WheelEvent) => {
			// A non-passive listener keeps wheel zoom inside the preview instead of scrolling the page.
			event.preventDefault();
			if (event.deltaY) changeZoom(event.deltaY < 0 ? 1.15 : 1 / 1.15);
		};
		element.addEventListener('wheel', onWheel, { passive: false });
		return () => element.removeEventListener('wheel', onWheel);
	}, []);

	return (
		<div>
			<div
				ref={viewport}
				className='h-[65vh] overflow-auto select-none'
				style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default', touchAction: 'none' }}
				tabIndex={0}
				aria-label='Image preview; use the mouse wheel to zoom and drag to pan'
				onPointerDown={(event) => {
					if (zoom <= 1 || event.button !== 0 || drag.current) return;
					const element = event.currentTarget;
					drag.current = {
						pointerId: event.pointerId,
						x: event.clientX,
						y: event.clientY,
						left: element.scrollLeft,
						top: element.scrollTop,
					};
					// Capture keeps dragging reliable outside the viewport; native scroll bounds constrain panning.
					element.setPointerCapture(event.pointerId);
					setDragging(true);
				}}
				onPointerMove={(event) => {
					const start = drag.current;
					if (!start || start.pointerId !== event.pointerId) return;
					event.currentTarget.scrollLeft = start.left - (event.clientX - start.x);
					event.currentTarget.scrollTop = start.top - (event.clientY - start.y);
				}}
				onPointerUp={(event) => {
					if (event.currentTarget.hasPointerCapture(event.pointerId)) {
						event.currentTarget.releasePointerCapture(event.pointerId);
					}
				}}
				onLostPointerCapture={() => {
					drag.current = null;
					setDragging(false);
				}}
			>
				<div style={{ width: `${zoom * 100}%`, height: `${zoom * 65}vh` }}>
					<AttachmentImage src={src} name={name} className='w-full h-full' />
				</div>
			</div>
		</div>
	);
}

// Share the same preview for draft, sent, and historical attachments.
export function ImageAttachmentPreview({ src, name, type }: { src: string; name: string; type?: string }) {
	if (type === 'application/pdf') {
		return (
			<Tooltip content={name} triggerType='span' disableHelpCursor>
				<span
					role='img'
					tabIndex={0}
					aria-label={`PDF attachment: ${name}`}
					className='flex w-10 h-10 items-center justify-center rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500'
				>
					<FileText size={20} aria-hidden='true' />
				</span>
			</Tooltip>
		);
	}
	return (
		<Dialog
			title='Image preview'
			sizeX='lg'
			slimHeader
			minimalHeaderStyle
			actionButtons={[]}
			trigger={
				<button
					type='button'
					aria-label={`Preview ${name}`}
					className='block rounded-lg overflow-hidden focus-visible:ring-2 focus-visible:ring-blue-500'
				>
					<AttachmentImage src={src} name={name} className='w-24 h-24' thumbnail />
				</button>
			}
		>
			<ZoomableImage src={src} name={name} />
		</Dialog>
	);
}
